/**
 * PrintFileSelfHealSection - per-location print-file self-heal configuration.
 *
 * When a print batch is sent and a print file is missing on the active cloud,
 * self-heal copies it from a backup cloud (and/or raises an Issue). This panel
 * configures that per the currently selected location:
 *  - master Enable toggle (off = "None")
 *  - Applies to: Standard / On-Demand / Both
 *  - Self-heal covers: Standard / On-Demand / Both
 *  - Backup source: GCS (or none)
 *  - Slack alerts on missing files
 *  - the STANDARDS auto-heal toggle (BLU-827) — its own group, independent of
 *    the master Enable toggle
 *  - whether the print station trusts the batch-time file check (its own group
 *    at the end of the panel — the backend gate for it is independent of the
 *    master Enable toggle, so it must never be hidden behind it)
 *
 * Mirrors CxNotificationSettingsSection's load/dirty/save shape. Location is
 * resolved from LocationContext (same as the Issues surface). Settings are
 * keyed by location, so a no-location state shows a prompt.
 *
 * "Self-heal covers" IS NOT A FORMAT DECLARATION and never was. The stored
 * values `arx4`/`png`/`both` are read by print-file-self-heal.ts as a KIND
 * filter (`arx4` = standards, `png` = customs), which is why the label reads
 * "Self-heal covers: Standard / On-Demand / Both". The stored values, the
 * mutation input and the backend are unchanged; only the labels are. This
 * matters because an operator "matching" a format setting by picking `.png`
 * here would switch STANDARDS self-heal off.
 *
 * BLU-877 (plan 84-08): the BLU-827 standards auto-heal toggle MOVED HERE from
 * the retired PrintFileFormatSection, which held it as a lodger. That section
 * existed to declare a per-location print file format; that declaration is
 * retired (the per-agent `agent_tokens.printer_format` is the source of truth
 * now), so the section went — but the machinery this toggle gates is very much
 * alive, and is the batch-time fallback for the png standards lane. Deleting it
 * with its host would have silently disarmed an operator kill switch for live
 * machinery. It lives beside "At the print station" for the same reason that
 * group does: neither depends on the master Enable toggle.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Save, MapPin, PlugZap, CheckCircle2, XCircle, Send, AlertTriangle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SlackChannelPicker } from '@/features/organization/components/SlackChannelPicker';
import { SlackMentionPicker } from '@/components/SlackMentionPicker';
import { SettingRow } from './console/SettingRow';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useLocation } from '@/contexts/LocationContext';

type Scope = 'standard' | 'ondemand' | 'both';
type Extensions = 'arx4' | 'png' | 'both';
type BackupCloud = 'gcs' | 'tencent_cos' | 'azure' | null;
// COS regions a heal write can target — mirrors TENCENT_COS_REGIONS + the DB
// allowlist (migration 20260626000000). ap-singapore is the replicate-to-Shanghai
// target used to dodge the mainland upload throttle.
type HealWriteRegion =
  | 'ap-guangzhou'
  | 'ap-shanghai'
  | 'ap-shenzhen-fsi'
  | 'ap-beijing'
  | 'ap-singapore';
const HEAL_WRITE_REGIONS: HealWriteRegion[] = [
  'ap-singapore',
  'ap-shanghai',
  'ap-guangzhou',
  'ap-shenzhen-fsi',
  'ap-beijing',
];
const HEAL_WRITE_NONE = 'none';
// A plain bucket name (mirrors the backend zod + DB validation).
// Tencent COS <name>-<appid> format — mirrors the backend zod + DB check. The
// same-account appid constraint is enforced server-side (needs the org's vault
// bucket), so the form only validates the shape.
const BUCKET_NAME_RE = /^[a-z0-9-]+-\d{10,}$/;
// Slack incoming-webhook shape — mirrors the backend's ALLOWED_WEBHOOK_HOSTS
// (slack-notifications.ts) for the inline hint; the backend still does the
// real SSRF-safe validation (validateSlackWebhookUrl) on save/test.
const WEBHOOK_URL_RE = /^https:\/\/hooks\.slack\.com\//;

interface SelfHealSettingsSectionProps {
  /** Reserved for parity with sibling sections; the panel renders inline. */
  embedded?: boolean;
}

export function PrintFileSelfHealSection(_props: SelfHealSettingsSectionProps) {
  const { t } = useTranslation('settings');
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id ?? null;

  const [enabled, setEnabled] = useState(false);
  const [scope, setScope] = useState<Scope>('standard');
  const [extensions, setExtensions] = useState<Extensions>('arx4');
  const [backupCloud, setBackupCloud] = useState<BackupCloud>('gcs');
  const [customSourceHealEnabled, setCustomSourceHealEnabled] = useState(false);
  const [customPreMirrorEnabled, setCustomPreMirrorEnabled] = useState(false);
  const [productionManifestReadEnabled, setProductionManifestReadEnabled] = useState(false);
  // BLU-827, rehomed here by BLU-877. Tracked SEPARATELY from `hasChanges`
  // because it is a separate save unit against a separate, owner-gated
  // mutation — exactly as it was in the section it moved from. A single shared
  // flag would let a self-heal save clear this toggle's pending edit.
  const [standardsHeal, setStandardsHeal] = useState(false);
  // Filed notification (slackEnabled = the filed-enable flag) + resolved
  // notification, each with its own channel + editable message template.
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [filedChannel, setFiledChannel] = useState<string | null>(null);
  const [filedWebhookUrl, setFiledWebhookUrl] = useState('');
  const [filedTemplate, setFiledTemplate] = useState<string | null>(null);
  const [resolvedNotifyEnabled, setResolvedNotifyEnabled] = useState(false);
  const [resolvedChannel, setResolvedChannel] = useState<string | null>(null);
  const [resolvedWebhookUrl, setResolvedWebhookUrl] = useState('');
  const [resolvedTemplate, setResolvedTemplate] = useState<string | null>(null);
  const [notifyAutoHeals, setNotifyAutoHeals] = useState(true);
  const [resolvedDigestEnabled, setResolvedDigestEnabled] = useState(false);
  // Default TRUE — matches the server-side `!== false` read, so a location
  // that has never saved this key shows the state it actually has.
  const [filedDigestEnabled, setFiledDigestEnabled] = useState(true);
  const [filedMentionUserIds, setFiledMentionUserIds] = useState<string[]>([]);
  const [healWriteRegion, setHealWriteRegion] = useState<HealWriteRegion | null>(null);
  const [healWriteBucket, setHealWriteBucket] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  /** Which location the fields currently hold values for — see the effect below. */
  const hydratedFor = useRef<string | null>(null);
  // Result of the last "Test ingest cloud" run: null = not run; true/false = reachable.
  const [ingestTestResult, setIngestTestResult] = useState<boolean | null>(null);

  const settingsQuery = trpc.productionSettings.getLocationSelfHealSettings.useQuery(
    { locationId: locationId ?? '' },
    { enabled: !!locationId }
  );

  const updateMutation = trpc.productionSettings.updateLocationSelfHealSettings.useMutation({
    onSuccess: () => {
      toast.success(t('production.printFileSelfHeal.saved'));
      setHasChanges(false);
      settingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(t('production.printFileSelfHeal.saveFailed', { message: error.message }));
    },
  });

  // BLU-827 (rehomed): its OWN mutation. The standards heal is owner-gated
  // server-side because at a shared 3PL floor the OWNER's bridge does the RIP
  // work, so it can never ride on updateLocationSelfHealSettings without
  // retroactively tightening every pre-existing self-heal key.
  const healMutation = trpc.productionSettings.setLocationStandardsMetafieldHeal.useMutation({
    onSuccess: () => {
      toast.success(t('production.printFileSelfHeal.saved'));
      void settingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const testIngestMutation = trpc.productionSettings.testHealWriteTarget.useMutation({
    onSuccess: (res) => {
      setIngestTestResult(res.ok);
      if (res.ok) {
        toast.success(t('production.printFileSelfHeal.writeTestOk'));
      } else {
        // Map the raw reason enum to plain operator language (no jargon in toasts).
        const reason = t(`production.printFileSelfHeal.writeTestReason.${res.reason}`);
        toast.error(t('production.printFileSelfHeal.writeTestFailed', { reason }));
      }
    },
    onError: (error) => {
      setIngestTestResult(false);
      toast.error(t('production.printFileSelfHeal.writeTestError', { message: error.message }));
    },
  });

  // Separate mutation instances per channel so a filed test doesn't grey out the
  // resolved Test button (mirrors NotificationSettings' per-channel pattern).
  const testFiledChannel = trpc.slackThreadbot.testChannel.useMutation({
    onSuccess: () => toast.success(t('production.printFileSelfHeal.testSent')),
    onError: (error) => toast.error(error.message),
  });
  const testResolvedChannel = trpc.slackThreadbot.testChannel.useMutation({
    onSuccess: () => toast.success(t('production.printFileSelfHeal.testSent')),
    onError: (error) => toast.error(error.message),
  });

  // Per-event webhook test — separate mutation instances so a filed test
  // doesn't grey out the resolved Test button (same pattern as the channel
  // tests above).
  const testFiledWebhook = trpc.productionSettings.testPrintFileNotifWebhook.useMutation({
    onSuccess: (res) => {
      if (res.sent) {
        toast.success(t('production.printFileSelfHeal.webhookTestSent'));
      } else {
        toast.error(t('production.printFileSelfHeal.webhookTestFailed', { message: res.error ?? '' }));
      }
    },
    onError: (error) => toast.error(error.message),
  });
  const testResolvedWebhook = trpc.productionSettings.testPrintFileNotifWebhook.useMutation({
    onSuccess: (res) => {
      if (res.sent) {
        toast.success(t('production.printFileSelfHeal.webhookTestSent'));
      } else {
        toast.error(t('production.printFileSelfHeal.webhookTestFailed', { message: res.error ?? '' }));
      }
    },
    onError: (error) => toast.error(error.message),
  });

  // BLU-827 (rehomed by BLU-877). Dirtiness derived from VALUE, not from "was
  // this control touched": a touch-based flag claimed an unsaved change for a
  // toggle the operator flipped and put straight back, then fired a no-op
  // owner-gated mutation on Save.
  const serverStandardsHeal = settingsQuery.data?.standardsMetafieldHealEnabled ?? false;
  const standardsHealDirty = !!settingsQuery.data && standardsHeal !== serverStandardsHeal;
  // Owner-ness is computed server-side and returned as a boolean (never the
  // owner's org id). At a shared 3PL floor the OWNER's declaration governs the
  // CLIENT's garments and the OWNER's bridge does the RIP work, so a non-owner
  // sees the toggle's state but cannot change it.
  const isLocationOwner = settingsQuery.data?.isLocationOwner ?? false;
  // HONESTY FLAG (server-side): the settings read is filtered by the CALLER's
  // org, so a junctioned client org reading a location it does not own gets no
  // row and every value here falls back to its default. A control whose backing
  // value we could not read must not render at all — "Off" would be an artefact
  // of the org filter presented as a fact about the floor.
  const settingsReadable = settingsQuery.data?.settingsReadable ?? false;

  useEffect(() => {
    // (B7) guard on `hasChanges`. Other surfaces read the SAME
    // getLocationSelfHealSettings cache entry, so a refetch they trigger must
    // not reset every field here and clear the dirty flag, silently discarding
    // in-progress self-heal edits.
    //
    // A LOCATION SWITCH overrides that guard. This panel never remounts
    // (ProductionSettingsPage renders it without a key), so without `hydratedFor`
    // a dirty edit at location A would survive the switch to B and Save would
    // write A's ~20 keys — Slack webhooks included — onto B.
    if (!settingsQuery.data) return;
    const switched = hydratedFor.current !== locationId;
    if (switched || !hasChanges) {
      setEnabled(settingsQuery.data.enabled);
      setScope(settingsQuery.data.scope as Scope);
      setExtensions(settingsQuery.data.extensions as Extensions);
      setBackupCloud(settingsQuery.data.backupCloud as BackupCloud);
      setCustomSourceHealEnabled(settingsQuery.data.customSourceHealEnabled);
      setCustomPreMirrorEnabled(settingsQuery.data.customPreMirrorEnabled);
      setProductionManifestReadEnabled(settingsQuery.data.productionManifestReadEnabled);
      setSlackEnabled(settingsQuery.data.slackEnabled);
      setFiledChannel(settingsQuery.data.filedChannel ?? null);
      setFiledWebhookUrl(settingsQuery.data.filedWebhookUrl ?? '');
      setFiledTemplate(settingsQuery.data.filedTemplate ?? null);
      setResolvedNotifyEnabled(settingsQuery.data.resolvedNotifyEnabled);
      setResolvedChannel(settingsQuery.data.resolvedChannel ?? null);
      setResolvedWebhookUrl(settingsQuery.data.resolvedWebhookUrl ?? '');
      setResolvedTemplate(settingsQuery.data.resolvedTemplate ?? null);
      setNotifyAutoHeals(settingsQuery.data.notifyAutoHeals);
      setResolvedDigestEnabled(settingsQuery.data.resolvedDigestEnabled);
      setFiledDigestEnabled(settingsQuery.data.filedDigestEnabled);
      setFiledMentionUserIds(settingsQuery.data.filedMentionUserIds);
      setHealWriteRegion((settingsQuery.data.healWriteRegion as HealWriteRegion | null) ?? null);
      setHealWriteBucket(settingsQuery.data.healWriteBucket ?? '');
      setHasChanges(false);
    }
    // The rehomed standards-heal toggle hydrates under its OWN guard, so a
    // refetch can refresh it while the self-heal half is dirty, and vice versa.
    // A location switch overrides both — this panel never remounts, so an
    // unguarded value from location A would otherwise be saved onto B.
    if (switched || !standardsHealDirty) {
      setStandardsHeal(settingsQuery.data.standardsMetafieldHealEnabled);
    }
    hydratedFor.current = locationId;
    // `hasChanges` / `standardsHealDirty` are deliberately NOT dependencies:
    // they are guards read at hydration time, not inputs that should
    // re-trigger hydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data, locationId]);

  // The heal-write target needs BOTH region + bucket; a half-filled pair is
  // invalid (would silently fall back to the active cloud).
  const trimmedBucket = healWriteBucket.trim();
  const bucketInvalid = trimmedBucket.length > 0 && !BUCKET_NAME_RE.test(trimmedBucket);
  const healWritePairIncomplete =
    (healWriteRegion !== null && trimmedBucket.length === 0) ||
    (healWriteRegion === null && trimmedBucket.length > 0);
  const trimmedFiledWebhookUrl = filedWebhookUrl.trim();
  const trimmedResolvedWebhookUrl = resolvedWebhookUrl.trim();
  const filedWebhookInvalid = trimmedFiledWebhookUrl.length > 0 && !WEBHOOK_URL_RE.test(trimmedFiledWebhookUrl);
  const resolvedWebhookInvalid = trimmedResolvedWebhookUrl.length > 0 && !WEBHOOK_URL_RE.test(trimmedResolvedWebhookUrl);
  // These four checks all belong to fields that only render inside the
  // `enabled` block. With the master switch OFF their inline errors are off
  // screen, so letting them gate Save would grey out the button with no
  // visible reason — and the station toggle below the block is always visible
  // and always saveable. Validate only what the operator can actually see.
  const dependentFieldsInvalid =
    bucketInvalid || healWritePairIncomplete || filedWebhookInvalid || resolvedWebhookInvalid;
  // BLU-825: "Applies to" (scope) and "Self-heal covers" (extensions, a kind
  // filter) are ANDed by standardInScope/customInScope. A disjoint pair leaves
  // self-heal probing NOTHING — advisory, not a hard gate: it is a legitimate
  // way to park the feature, it just must not be silent.
  const coverageStandard = extensions === 'arx4' || extensions === 'both';
  const coverageOndemand = extensions === 'png' || extensions === 'both';
  const coverageEmpty =
    (scope === 'standard' && !coverageStandard) || (scope === 'ondemand' && !coverageOndemand);
  const saveDisabled = updateMutation.isPending || (enabled && dependentFieldsInvalid);
  /**
   * The BATCH-SWEEP path of the standards heal is gated by three knobs that
   * used to live in a DIFFERENT card from the toggle — self-heal on, "Applies
   * to" including standard products, and "Self-heal covers" including .arx4 —
   * so an operator could arm the toggle and see nothing happen with no
   * explanation. After the BLU-877 rehome all four are in this card, and the
   * hint is read off the LIVE draft values rather than the persisted ones, so
   * turning the master switch on clears the hint immediately instead of after
   * a save. Name the three knobs when they do not line up, and say the
   * per-issue Re-check button works either way, because that is the path that
   * actually fires today.
   */
  const sweepPreconditionsMet =
    enabled && scope !== 'ondemand' && (extensions === 'arx4' || extensions === 'both');
  const showSweepHint = standardsHeal && !sweepPreconditionsMet;

  const handleSave = () => {
    if (!locationId || saveDisabled) return;
    // Each half fires ONLY when its own flag is set. An untouched half must
    // never be rewritten — least of all the ~20-key self-heal document, which
    // would otherwise be re-sent every time somebody flipped one toggle.
    if (standardsHealDirty && isLocationOwner && settingsReadable) {
      healMutation.mutate({ locationId, enabled: standardsHeal });
    }
    if (!hasChanges) return;
    // Counterpart to the gate above: the server validates these fields whatever
    // `enabled` says, so an invalid draft stranded behind the collapsed block
    // must not travel with the save. Nothing valid is ever dropped — only a
    // value the operator can neither see nor fix reverts to what is persisted.
    const persisted = settingsQuery.data;
    const dropHiddenDraft = !enabled;
    // Send the pair together: both set = route writes there; both null = direct
    // to the active cloud. (A half-filled pair is blocked by saveDisabled while
    // the fields are visible.)
    const healWriteDraftUnusable = dropHiddenDraft && (bucketInvalid || healWritePairIncomplete);
    const region = healWriteDraftUnusable
      ? ((persisted?.healWriteRegion as HealWriteRegion | null) ?? null)
      : trimmedBucket.length > 0
        ? healWriteRegion
        : null;
    const bucket = healWriteDraftUnusable
      ? (persisted?.healWriteBucket ?? null)
      : healWriteRegion !== null
        ? trimmedBucket
        : null;
    const filedWebhookForSave =
      dropHiddenDraft && filedWebhookInvalid
        ? (persisted?.filedWebhookUrl ?? null)
        : trimmedFiledWebhookUrl || null;
    const resolvedWebhookForSave =
      dropHiddenDraft && resolvedWebhookInvalid
        ? (persisted?.resolvedWebhookUrl ?? null)
        : trimmedResolvedWebhookUrl || null;
    updateMutation.mutate({
      locationId,
      enabled,
      scope,
      extensions,
      backupCloud,
      customSourceHealEnabled,
      customPreMirrorEnabled,
      productionManifestReadEnabled,
      slackEnabled,
      filedChannel,
      filedWebhookUrl: filedWebhookForSave,
      filedTemplate,
      resolvedNotifyEnabled,
      resolvedChannel,
      resolvedWebhookUrl: resolvedWebhookForSave,
      resolvedTemplate,
      notifyAutoHeals,
      resolvedDigestEnabled,
      filedDigestEnabled,
      filedMentionUserIds,
      healWriteRegion: region,
      healWriteBucket: bucket && bucket.length > 0 ? bucket : null,
    });
  };

  // No location selected — the settings are per-location.
  if (!locationId) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
        <MapPin className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          {t('production.printFileSelfHeal.noLocation')}
        </p>
      </div>
    );
  }

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  // A failed read must NEVER fall through to the form below: every field would
  // render its hard-coded default as if it were the saved state, next to a live
  // Save button that would then write those defaults over the ~20 real keys —
  // Slack webhooks and the heal-write target included.
  if (settingsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>{t('production.printFileSelfHeal.loadFailedTitle')}</AlertTitle>
        <AlertDescription className="flex items-center gap-2">
          {t('production.printFileSelfHeal.loadFailedBody')}
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => void settingsQuery.refetch()}
          >
            <RefreshCw className="mr-1 h-3 w-3" aria-hidden="true" />
            {t('production.common.retry')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <p className="max-w-prose text-sm text-muted-foreground">
        {t('production.printFileSelfHeal.description', { location: selectedLocation?.name ?? '' })}
      </p>

      {/* Master enable */}
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label htmlFor="self-heal-enabled" className="font-medium">
            {t('production.printFileSelfHeal.enableTitle')}
          </Label>
          <p className="text-sm text-muted-foreground">
            {t('production.printFileSelfHeal.enableDesc')}
          </p>
        </div>
        <Switch
          id="self-heal-enabled"
          checked={enabled}
          onCheckedChange={(v) => {
            setEnabled(v);
            setHasChanges(true);
          }}
        />
      </div>

      {/* Dependent settings (only when enabled) */}
      {enabled && (
        <div className="motion-safe:animate-fade-in space-y-4 rounded-lg border border-dashed p-4">
          {/* Applies to */}
          <div className="space-y-1.5">
            <Label htmlFor="self-heal-scope" className="text-sm font-medium">
              {t('production.printFileSelfHeal.scopeLabel')}
            </Label>
            <Select
              value={scope}
              onValueChange={(v) => {
                setScope(v as Scope);
                setHasChanges(true);
              }}
            >
              <SelectTrigger id="self-heal-scope" className="w-full sm:max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">
                  {t('production.printFileSelfHeal.scopeStandard')}
                </SelectItem>
                <SelectItem value="ondemand">
                  {t('production.printFileSelfHeal.scopeOndemand')}
                </SelectItem>
                <SelectItem value="both">
                  {t('production.printFileSelfHeal.scopeBoth')}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('production.printFileSelfHeal.scopeHelp')}
            </p>
          </div>

          {/* Self-heal covers — a KIND filter wearing extension clothing. The
              stored values stay `arx4`/`png`/`both`; the backend maps `arx4` to
              standards and `png` to customs (print-file-self-heal.ts's
              standardInScope/customInScope). Only the labels changed (BLU-825). */}
          <div className="space-y-1.5">
            <Label htmlFor="self-heal-ext" className="text-sm font-medium">
              {t('production.printFileSelfHeal.extLabel')}
            </Label>
            <Select
              value={extensions}
              onValueChange={(v) => {
                setExtensions(v as Extensions);
                setHasChanges(true);
              }}
            >
              <SelectTrigger
                id="self-heal-ext"
                className="w-full sm:max-w-xs"
                // The consequence is wired up alongside the explanation, but
                // only while the warning exists — an idref pointing at an absent
                // element is silently dropped.
                aria-describedby={
                  coverageEmpty ? 'self-heal-ext-help self-heal-ext-warn' : 'self-heal-ext-help'
                }
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="arx4">
                  {t('production.printFileSelfHeal.scopeStandard')}
                </SelectItem>
                <SelectItem value="png">
                  {t('production.printFileSelfHeal.scopeOndemand')}
                </SelectItem>
                <SelectItem value="both">{t('production.printFileSelfHeal.extBoth')}</SelectItem>
              </SelectContent>
            </Select>
            <p id="self-heal-ext-help" className="max-w-prose text-xs text-muted-foreground">
              {t('production.printFileSelfHeal.extHelp')}
            </p>
            {/* "Applies to" and "Self-heal covers" are ANDed server-side, so a
                disjoint pair (e.g. Standard x On-Demand) silently heals nothing
                at all. Today that combination is saveable with no feedback. */}
            {coverageEmpty && (
              <p
                id="self-heal-ext-warn"
                className="max-w-prose text-xs text-amber-700 dark:text-amber-400"
                role="status"
              >
                <span className="inline-flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {t('production.printFileSelfHeal.coverageEmptyWarning')}
                </span>
              </p>
            )}
          </div>

          {/* Backup source */}
          <div className="space-y-1.5">
            <Label htmlFor="self-heal-backup" className="text-sm font-medium">
              {t('production.printFileSelfHeal.backupLabel')}
            </Label>
            <Select
              value={backupCloud ?? 'none'}
              onValueChange={(v) => {
                setBackupCloud(
                  v === 'gcs' || v === 'tencent_cos' || v === 'azure' ? v : null,
                );
                setHasChanges(true);
              }}
            >
              <SelectTrigger id="self-heal-backup" className="w-full sm:max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gcs">
                  {t('production.printFileSelfHeal.backupGcs')}
                </SelectItem>
                <SelectItem value="tencent_cos">
                  {t('production.printFileSelfHeal.backupTencent')}
                </SelectItem>
                {/* Azure is container-based — the folder-based heal loop can't
                    use it, so it's shown (to display a pre-existing value) but
                    not selectable. See resolveBackupStorage in print-file-self-heal.ts. */}
                <SelectItem value="azure" disabled>
                  {t('production.printFileSelfHeal.backupAzure')}
                </SelectItem>
                <SelectItem value="none">
                  {t('production.printFileSelfHeal.backupNone')}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('production.printFileSelfHeal.backupHelp')}
            </p>
          </div>

          {/* Custom-source auto-heal — batch-time, re-fetches the artwork
              source URL instead of leaving a fresh custom issue for the
              bridge/an operator. */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label htmlFor="self-heal-custom-source" className="font-medium">
                {t('production.printFileSelfHeal.customSourceTitle')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('production.printFileSelfHeal.customSourceDesc')}
              </p>
              {customSourceHealEnabled && (scope === 'standard' || extensions === 'arx4') && (
                <p className="mt-1 text-sm text-amber-700">
                  {t('production.printFileSelfHeal.customSourceScopeHint')}
                </p>
              )}
            </div>
            <Switch
              id="self-heal-custom-source"
              checked={customSourceHealEnabled}
              onCheckedChange={(v) => {
                setCustomSourceHealEnabled(v);
                setHasChanges(true);
              }}
            />
          </div>

          {/* Custom PNG pre-mirror (BLU-743) — proactively mirrors custom
              artwork to the print cloud when a batch is created, before any
              probe finds it missing. */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label htmlFor="self-heal-custom-premirror" className="font-medium">
                {t('production.printFileSelfHeal.customPreMirrorTitle')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('production.printFileSelfHeal.customPreMirrorDesc')}
              </p>
            </div>
            <Switch
              id="self-heal-custom-premirror"
              checked={customPreMirrorEnabled}
              onCheckedChange={(v) => {
                setCustomPreMirrorEnabled(v);
                setHasChanges(true);
              }}
            />
          </div>

          {/* Notifications — per-location Slack alerts for filed + resolved
              print-file issues, each with its own channel + editable template.
              One bounding border for the group; filed + resolved are separated by
              spacing and a single hairline divider (no nested boxes). */}
          <div className="space-y-4 rounded-lg border p-3">
            <div>
              <Label className="font-medium">
                {t('production.printFileSelfHeal.notifyTitle')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('production.printFileSelfHeal.notifyDesc')}
              </p>
            </div>

            {/* a. Filed — when a print-file issue is raised. */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="self-heal-filed" className="font-medium">
                    {t('production.printFileSelfHeal.filedTitle')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('production.printFileSelfHeal.filedDesc')}
                  </p>
                </div>
                <Switch
                  id="self-heal-filed"
                  checked={slackEnabled}
                  onCheckedChange={(v) => {
                    setSlackEnabled(v);
                    setHasChanges(true);
                  }}
                />
              </div>
              {slackEnabled && (
                <div className="motion-safe:animate-fade-in space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t('production.printFileSelfHeal.channelLabel')}
                    </Label>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <SlackChannelPicker
                          value={filedChannel}
                          onChange={(next) => {
                            setFiledChannel(next);
                            setHasChanges(true);
                          }}
                          enabled={slackEnabled}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={!filedChannel || testFiledChannel.isPending}
                        onClick={() => {
                          if (!filedChannel) {
                            toast.error(t('production.printFileSelfHeal.pickChannelFirst'));
                            return;
                          }
                          testFiledChannel.mutate({ channel: filedChannel });
                        }}
                      >
                        {testFiledChannel.isPending ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {t('production.printFileSelfHeal.testButton')}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('production.printFileSelfHeal.webhookFallbackHint')}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="self-heal-filed-webhook" className="text-xs font-medium">
                      {t('production.printFileSelfHeal.webhookLabel')}
                    </Label>
                    <div className="flex items-start gap-2">
                      <Input
                        id="self-heal-filed-webhook"
                        value={filedWebhookUrl}
                        placeholder="https://hooks.slack.com/services/…"
                        className="flex-1 font-mono text-sm"
                        aria-invalid={filedWebhookInvalid || undefined}
                        onChange={(e) => {
                          setFiledWebhookUrl(e.target.value);
                          setHasChanges(true);
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={!trimmedFiledWebhookUrl || filedWebhookInvalid || testFiledWebhook.isPending}
                        onClick={() => testFiledWebhook.mutate({ webhookUrl: trimmedFiledWebhookUrl, event: 'filed' })}
                      >
                        {testFiledWebhook.isPending ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {t('production.printFileSelfHeal.testButton')}
                      </Button>
                    </div>
                    {filedWebhookInvalid && (
                      <p className="text-xs text-destructive" role="alert">
                        {t('production.printFileSelfHeal.webhookInvalid')}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t('production.printFileSelfHeal.filedWebhookCaption')}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="self-heal-filed-template" className="text-xs font-medium">
                      {t('production.printFileSelfHeal.templateLabel')}
                    </Label>
                    <Textarea
                      id="self-heal-filed-template"
                      value={filedTemplate ?? ''}
                      rows={3}
                      className="font-mono text-sm"
                      placeholder={t('production.printFileSelfHeal.filedTemplatePlaceholder')}
                      onChange={(e) => {
                        setFiledTemplate(e.target.value || null);
                        setHasChanges(true);
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('production.printFileSelfHeal.templateHint')}
                    </p>
                  </div>
                  {/* Group every filed alert into one message per 30-minute
                      window. Plain row (no box) — a sub-option of Filed, same
                      styling as the Resolved sub-options below. */}
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="self-heal-filed-digest" className="text-sm font-medium">
                        {t('production.printFileSelfHeal.filedDigestTitle')}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {t('production.printFileSelfHeal.filedDigestDesc')}
                      </p>
                    </div>
                    <Switch
                      id="self-heal-filed-digest"
                      checked={filedDigestEnabled}
                      onCheckedChange={(v) => {
                        setFiledDigestEnabled(v);
                        setHasChanges(true);
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t('production.printFileSelfHeal.mentionLabel')}
                    </Label>
                    <SlackMentionPicker
                      value={filedMentionUserIds}
                      onChange={(next) => {
                        setFiledMentionUserIds(next);
                        setHasChanges(true);
                      }}
                      enabled={slackEnabled}
                      hint={t('production.printFileSelfHeal.mentionHint')}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('production.printFileSelfHeal.mentionCaption')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* b. Resolved — when a filed issue is fixed (upload/recheck/heal).
                Hairline divider (border-t) separates it from Filed above. */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="self-heal-resolved" className="font-medium">
                    {t('production.printFileSelfHeal.resolvedTitle')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('production.printFileSelfHeal.resolvedDesc')}
                  </p>
                </div>
                <Switch
                  id="self-heal-resolved"
                  checked={resolvedNotifyEnabled}
                  onCheckedChange={(v) => {
                    setResolvedNotifyEnabled(v);
                    setHasChanges(true);
                  }}
                />
              </div>
              {resolvedNotifyEnabled && (
                <div className="motion-safe:animate-fade-in space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t('production.printFileSelfHeal.channelLabel')}
                    </Label>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <SlackChannelPicker
                          value={resolvedChannel}
                          onChange={(next) => {
                            setResolvedChannel(next);
                            setHasChanges(true);
                          }}
                          enabled={resolvedNotifyEnabled}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={!resolvedChannel || testResolvedChannel.isPending}
                        onClick={() => {
                          if (!resolvedChannel) {
                            toast.error(t('production.printFileSelfHeal.pickChannelFirst'));
                            return;
                          }
                          testResolvedChannel.mutate({ channel: resolvedChannel });
                        }}
                      >
                        {testResolvedChannel.isPending ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {t('production.printFileSelfHeal.testButton')}
                      </Button>
                    </div>
                    {/* Resolved alerts have NO legacy-webhook fallback (unlike filed),
                        so with neither a channel nor a per-event webhook set nothing
                        sends at all — say so. A resolved webhook below counts as a
                        destination, same as the channel. */}
                    {!resolvedChannel && !trimmedResolvedWebhookUrl && (
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        {t('production.printFileSelfHeal.resolvedNoChannelHint')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="self-heal-resolved-webhook" className="text-xs font-medium">
                      {t('production.printFileSelfHeal.webhookLabel')}
                    </Label>
                    <div className="flex items-start gap-2">
                      <Input
                        id="self-heal-resolved-webhook"
                        value={resolvedWebhookUrl}
                        placeholder="https://hooks.slack.com/services/…"
                        className="flex-1 font-mono text-sm"
                        aria-invalid={resolvedWebhookInvalid || undefined}
                        onChange={(e) => {
                          setResolvedWebhookUrl(e.target.value);
                          setHasChanges(true);
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={!trimmedResolvedWebhookUrl || resolvedWebhookInvalid || testResolvedWebhook.isPending}
                        onClick={() => testResolvedWebhook.mutate({ webhookUrl: trimmedResolvedWebhookUrl, event: 'resolved' })}
                      >
                        {testResolvedWebhook.isPending ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {t('production.printFileSelfHeal.testButton')}
                      </Button>
                    </div>
                    {resolvedWebhookInvalid && (
                      <p className="text-xs text-destructive" role="alert">
                        {t('production.printFileSelfHeal.webhookInvalid')}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t('production.printFileSelfHeal.resolvedWebhookCaption')}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="self-heal-resolved-template" className="text-xs font-medium">
                      {t('production.printFileSelfHeal.templateLabel')}
                    </Label>
                    <Textarea
                      id="self-heal-resolved-template"
                      value={resolvedTemplate ?? ''}
                      rows={3}
                      className="font-mono text-sm"
                      placeholder={t('production.printFileSelfHeal.resolvedTemplatePlaceholder')}
                      onChange={(e) => {
                        setResolvedTemplate(e.target.value || null);
                        setHasChanges(true);
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('production.printFileSelfHeal.templateHint')}
                    </p>
                  </div>
                  {/* Silence the automatic (bridge / designs-folder) resolves —
                      manual resolves still ping regardless of this toggle. Plain
                      row (no box) — it's a sub-option of Resolved, not its own card. */}
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="self-heal-auto-heals" className="text-sm font-medium">
                        {t('production.printFileSelfHeal.autoHealsTitle')}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {t('production.printFileSelfHeal.autoHealsDesc')}
                      </p>
                    </div>
                    <Switch
                      id="self-heal-auto-heals"
                      checked={notifyAutoHeals}
                      onCheckedChange={(v) => {
                        setNotifyAutoHeals(v);
                        setHasChanges(true);
                      }}
                    />
                  </div>
                  {/* Batch resolved alerts into one Slack message per
                      30-minute window instead of one per print file. Plain
                      row, same sub-option styling as autoHeals above. */}
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="self-heal-resolved-digest" className="text-sm font-medium">
                        {t('production.printFileSelfHeal.resolvedDigestTitle')}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {t('production.printFileSelfHeal.resolvedDigestDesc')}
                      </p>
                    </div>
                    <Switch
                      id="self-heal-resolved-digest"
                      checked={resolvedDigestEnabled}
                      onCheckedChange={(v) => {
                        setResolvedDigestEnabled(v);
                        setHasChanges(true);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Heal write target (advanced) — the bucket heals WRITE to AND, since
              BLU-826, the bucket the backend asks "does the file exist?"
              (`BuildStorageResult.ingest`). The active cloud is only what the
              station GETs from. A wrong value here reads every file as absent. */}
          <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
            <div>
              <Label className="text-sm font-medium">
                {t('production.printFileSelfHeal.writeTargetTitle')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('production.printFileSelfHeal.writeTargetHelp')}
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="self-heal-write-region" className="text-xs font-medium">
                  {t('production.printFileSelfHeal.writeRegionLabel')}
                </Label>
                <Select
                  value={healWriteRegion ?? HEAL_WRITE_NONE}
                  onValueChange={(v) => {
                    setHealWriteRegion(v === HEAL_WRITE_NONE ? null : (v as HealWriteRegion));
                    setHasChanges(true);
                    setIngestTestResult(null); // a prior test no longer applies
                  }}
                >
                  <SelectTrigger id="self-heal-write-region">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={HEAL_WRITE_NONE}>
                      {t('production.printFileSelfHeal.writeRegionNone')}
                    </SelectItem>
                    {HEAL_WRITE_REGIONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="self-heal-write-bucket" className="text-xs font-medium">
                  {t('production.printFileSelfHeal.writeBucketLabel')}
                </Label>
                <Input
                  id="self-heal-write-bucket"
                  value={healWriteBucket}
                  placeholder="testthreadheads-1325746065"
                  className="font-mono"
                  aria-invalid={bucketInvalid || undefined}
                  onChange={(e) => {
                    setHealWriteBucket(e.target.value);
                    setHasChanges(true);
                    setIngestTestResult(null); // a prior test no longer applies
                  }}
                />
              </div>
            </div>
            {bucketInvalid && (
              <p className="text-xs text-destructive" role="alert">
                {t('production.printFileSelfHeal.writeBucketInvalid')}
              </p>
            )}
            {healWritePairIncomplete && (
              <p className="text-xs text-destructive" role="alert">
                {t('production.printFileSelfHeal.writePairIncomplete')}
              </p>
            )}
            {/* Test the ingest cloud BEFORE enabling — proves the bucket is
                reachable with the org's creds so a typo/dead bucket is caught
                here, not as a silent heal failure at runtime. */}
            {healWriteRegion !== null && !bucketInvalid && trimmedBucket.length > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={testIngestMutation.isPending}
                  onClick={() => {
                    setIngestTestResult(null);
                    testIngestMutation.mutate({ region: healWriteRegion, bucket: trimmedBucket });
                  }}
                >
                  {testIngestMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <PlugZap className="h-4 w-4" aria-hidden="true" />
                  )}
                  {t('production.printFileSelfHeal.writeTestButton')}
                </Button>
                {ingestTestResult === true && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700" role="status">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('production.printFileSelfHeal.writeTestReachable')}
                  </span>
                )}
                {ingestTestResult === false && (
                  <span className="inline-flex items-center gap-1 text-xs text-destructive" role="status">
                    <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('production.printFileSelfHeal.writeTestUnreachable')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recovery (BLU-827, rehomed here by BLU-877 plan 84-08). Deliberately
          OUTSIDE the `enabled` block, for the same reason the station group
          below is: the operator Re-check path on an individual issue fires
          whatever the master switch says, so nesting it would leave the setting
          silently active while hidden. Same weight as the master card (rounded
          border, no muted fill) — a sub-option skin would read as "greyed out
          because the master is off", which is exactly the dependency it does
          not have.

          Rendered only when the backing value was actually READ: a toggle
          showing "off" because the org filter matched no row is a lie about
          another org's floor. */}
      {settingsReadable && (
        <div
          role="group"
          aria-labelledby="standards-metafield-heal-group-title"
          className="rounded-lg border p-4"
        >
          <h4 id="standards-metafield-heal-group-title" className="text-sm font-medium">
            {t('production.standardsMetafieldHeal.groupLabel')}
          </h4>
          {!isLocationOwner && (
            <p className="max-w-prose pt-1 text-xs text-muted-foreground">
              {t('production.standardsMetafieldHeal.ownerOnly', {
                location: selectedLocation?.name ?? '',
              })}
            </p>
          )}
          {/* The description goes THROUGH SettingRow, which owns the id and
              renders it in the row's left column; the render-prop hands back
              the id the row actually rendered, so the precondition hint's id is
              appended to THAT rather than to a guess. No native `title` on the
              disabled control: the owner-only sentence is already on screen,
              and a `title` on a disabled wrapper is unreachable by keyboard. */}
          <SettingRow
            htmlFor="standards-metafield-heal"
            label={t('production.standardsMetafieldHeal.label')}
            description={t('production.standardsMetafieldHeal.help')}
          >
            {({ describedBy }) => (
              <Switch
                id="standards-metafield-heal"
                checked={standardsHeal}
                disabled={!isLocationOwner || healMutation.isPending}
                aria-describedby={
                  [describedBy, showSweepHint ? 'standards-metafield-heal-hint' : null]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                onCheckedChange={(v) => setStandardsHeal(v)}
              />
            )}
          </SettingRow>
          {showSweepHint && (
            <p
              id="standards-metafield-heal-hint"
              className="max-w-prose pt-1 text-xs text-amber-700 dark:text-amber-400"
              role="status"
            >
              <span className="inline-flex items-start gap-1">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t('production.standardsMetafieldHeal.preconditionHint')}
              </span>
            </p>
          )}
        </div>
      )}

      {/* At the print station (BLU-823) — deliberately OUTSIDE the `enabled`
          block: the backend gate (isManifestReadEnabled) does not depend on the
          self-heal master switch, and the batch-time file check is recorded
          unconditionally. Nesting it would leave the setting silently active
          while hidden. It therefore carries the SAME weight as the master card
          (rounded-lg border p-4, no muted fill) — a bg-muted/30 sub-option skin
          would read as "greyed out because the master is off", which is exactly
          the dependency this group does not have. */}
      <div
        role="group"
        aria-labelledby="self-heal-station-group-title"
        className="rounded-lg border p-4"
      >
        <h4 id="self-heal-station-group-title" className="text-sm font-medium">
          {t('production.printFileSelfHeal.stationGroupTitle')}
        </h4>
        <SettingRow
          htmlFor="self-heal-manifest-read"
          label={t('production.printFileSelfHeal.productionManifestReadTitle')}
          description={t('production.printFileSelfHeal.productionManifestReadDesc')}
        >
          {({ describedBy }) => (
            <Switch
              id="self-heal-manifest-read"
              checked={productionManifestReadEnabled}
              aria-describedby={describedBy}
              onCheckedChange={(v) => {
                setProductionManifestReadEnabled(v);
                setHasChanges(true);
              }}
            />
          )}
        </SettingRow>
      </div>

      {(hasChanges || standardsHealDirty) && (
        <div className="flex flex-col items-end gap-2 pt-2">
          {/* Advisory (not a hard gate): a configured write target that hasn't
              passed "Test ingest cloud" can still be saved, but flag it so it's a
              deliberate choice. A passing HeadBucket can't prove replication is
              on, so we don't block Save on it — the deferred active-cloud
              re-check is the real safety net. */}
          {enabled && healWriteRegion !== null && trimmedBucket.length > 0 && ingestTestResult !== true && (
            <p className="text-xs text-amber-700" role="status">
              {t('production.printFileSelfHeal.writeNotVerified')}
            </p>
          )}
          <Button
            onClick={handleSave}
            disabled={saveDisabled}
            className="h-12 gap-2 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {updateMutation.isPending || healMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            {t('production.common.saveChanges')}
          </Button>
        </div>
      )}
    </div>
  );
}
